import React from 'react'
import { roleLabel } from '../lib/permModules'
import { PRIVACY_URL, TERMS_URL, LICENCES_PATH } from '../lib/legal'
import FeedbackWidget from './FeedbackWidget'
import { EmergencyButton } from './EmergencyLauncher'
import { PageGuideButton } from './GuideHelpPanel'
import UserMenu from './UserMenu'
import UndoToast from './UndoToast'
import { canSeeAllHistory } from './HistoryLink'
import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { fetchRows, updateRow } from '../lib/api'
import { supabase } from '../lib/supabase'
import { searchRecords, openHit, isSearchShortcut } from '../lib/recordSearch'
import { pageHits, enterTarget } from '../lib/pageSearch'
import { getShipTz, shipNowOr, setShipTz, COMMON_ZONES } from '../lib/shipTime'
import { visibleItems, buildMenu, groupOf, groupBadge, loadOpen, saveOpen } from '../lib/navGroups'

function ShipTimeModal({ config, onClose }) {
  const [tz, setTz] = React.useState(config?.timezone || '')
  const [saving, setSaving] = React.useState(false)
  const [msg, setMsg] = React.useState('')
  const [, setTick] = React.useState(0)
  React.useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(id)
  }, [])

  const preview = shipNowOr(tz || null)

  async function save() {
    if (!config?.id) return
    setSaving(true); setMsg('')
    try {
      const { data, error } = await supabase.from('clients').update({ timezone: tz || null }).eq('id', config.id).select('id,timezone')
      if (error) throw new Error(error.message)
      if (!data || !data.length) throw new Error('Could not save - please try again.')
      setShipTz(tz || null)
      setMsg("Ship's time saved. Reload to apply everywhere.")
    } catch (e) { setMsg('Failed: ' + (e.message || 'unknown')) }
    finally { setSaving(false) }
  }

  const sel = { width: '100%', marginTop: 6, marginBottom: 14, padding: '9px 11px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, appearance: 'none' }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 12, padding: 24, width: 480, maxWidth: '92vw' }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>Ship&#39;s Time</div>
        <p style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 16 }}>
          MLC records are kept in the vessel&#39;s time, set by the Master &mdash; not the timezone of whatever
          device is open. Hours of Rest, Watch Schedule, Duty Rota and drill records take their day boundary
          from this. Change it when the ship&#39;s clocks change.
        </p>
        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
          {config?.vessel_name || 'Vessel'} Timezone
        </label>
        <select value={tz} onChange={e => setTz(e.target.value)} style={sel}>
          <option value="">Not set (device local time)</option>
          {COMMON_ZONES.map(z => <option key={z} value={z}>{z}</option>)}
        </select>
        <div style={{ border: '1px solid var(--line)', borderRadius: 8, padding: '12px 14px', marginBottom: 16, background: 'var(--bg2)' }}>
          <div style={{ fontSize: 10, letterSpacing: '.1em', color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 4 }}>
            {tz ? "Ship's time now" : 'Device local time now'}
          </div>
          <div style={{ fontSize: 22, fontWeight: 600, color: '#2E5BFF', fontVariantNumeric: 'tabular-nums' }}>{preview.hm}</div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>{preview.date}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose}>Close</button>
          <button className="btn btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
        {msg && <div style={{ marginTop: 12, fontSize: 12, color: msg.startsWith('Failed') ? '#b4313c' : 'var(--muted)' }}>{msg}</div>}
      </div>
    </div>
  )
}

function HeaderClock() {
  const [now, setNow] = React.useState(new Date())
  React.useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(id)
  }, [])
  // Ship's time when the vessel has a zone set; device time otherwise. Labelled either way.
  const tz = getShipTz()
  const opts = tz ? { timeZone: tz } : {}
  const date = now.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', ...opts })
  const time = now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, ...opts })
  return (
    <div style={{ textAlign: 'right', lineHeight: 1.2, marginRight: 4 }}>
      <div style={{ fontSize: 17, fontWeight: 500, color: 'var(--text)', fontVariantNumeric: 'tabular-nums', letterSpacing: '.04em' }}>{time}</div>
      <div style={{ fontSize: 10.5, color: 'var(--muted)' }}>{date}</div>
      <div style={{ fontSize: 8.5, letterSpacing: '.12em', color: tz ? '#2E5BFF' : 'var(--faint)', textTransform: 'uppercase', marginTop: 1 }}>
        {tz ? "Ship's Time" : 'Device Time'}
      </div>
    </div>
  )
}

function ThemeToggle() {
  const [theme, setTheme] = useState(() => (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme')) || 'dark')
  function toggle() {
    const next = theme === 'dark' ? 'light' : 'dark'
    setTheme(next)
    document.documentElement.setAttribute('data-theme', next)
    try { localStorage.setItem('hc_theme', next) } catch (e) {}
  }
  return (
    <button className="btn" onClick={toggle} style={{fontSize:14, padding:'7px 10px'}} title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}>
      {theme === 'dark' ? '☀️' : 'ἱ9'}
    </button>
  )
}


const PHONE_TABS = [
  { id: 'chat', label: 'AI chat', icon: 'i-spark' },
  { id: 'todo', label: 'To Do', icon: 'i-list' },
  { id: 'checklists', label: 'Checklists', icon: 'i-clipboard' },
  { id: 'weather', label: 'Weather', icon: 'i-cloud' },
  { id: 'hours_rest', label: 'Rest hours', icon: 'i-clock' },
]

export default function AppShell({ page, onNavigate, badges, children, title, subtitle, showNew, onNew, onRefresh }) {
  const { config, signOut , can, hasFeature, accountType, selectedVessel, isCaptain } = useAuth()
  const atFleet = accountType === 'company' && !selectedVessel
  // iPad landscape / small screens (1024-1279px): the sidebar starts as the icon rail, so pages get the width (ipad);
  // below 1024 the phone layout (drawer) is used and the rail is never shown there
  const [collapsed, setCollapsed] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1024 && window.innerWidth < 1280)
  const [mobileOpen, setMobileOpen] = useState(false)
  React.useEffect(() => { setMobileOpen(false) }, [page])
  const [q, setQ] = React.useState('')
  const [searchOpen, setSearchOpen] = React.useState(false)
  const searchRef = React.useRef(null)
  // "/" = the search (Cmd / Ctrl + K is the AI assistant only: FloatingAssistant.jsx)
  React.useEffect(() => {
    const h = (e) => {
      if (isSearchShortcut(e) && searchRef.current && searchRef.current.offsetParent !== null) { e.preventDefault(); searchRef.current.focus() }
      if (e.key === 'Escape') { setSearchOpen(false); setQ('') }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [])
  // records (equipment, inventory, crew, To Do, documents) - only the kinds this login may view: src/lib/recordSearch.js
  const [recHits, setRecHits] = React.useState([])
  const [recBusy, setRecBusy] = React.useState(false)
  React.useEffect(() => {
    const s = q.trim()
    if (s.length < 2 || (accountType === 'company' && !selectedVessel)) { setRecHits([]); setRecBusy(false); return }
    let live = true
    setRecBusy(true)
    const t = setTimeout(() => {
      searchRecords(supabase, s, { cid: config?.id, me: config?.crewName || config?.email, can, hasFeature })
        .then(h => { if (live) setRecHits(h) }).catch(() => { if (live) setRecHits([]) }).finally(() => { if (live) setRecBusy(false) })
    }, 250)
    return () => { live = false; clearTimeout(t) }
  }, [q, config?.id]) // eslint-disable-line
  // the entries this login may see (rules in src/lib/navGroups.js), then drawn as pinned items + collapsible groups
  const visibleNav = visibleItems({ accountType, selectedVessel, config, can, hasFeature, isCaptain }, canSeeAllHistory)
  const menu = buildMenu(visibleNav)
  const activeGroup = groupOf(page)
  const who = config?.email || config?.crewName || config?.id || ''
  // open groups: remembered per login; the group holding the current page opens when you get there. Phone/iPad drawer: only the current one.
  const [openG, setOpenG] = useState(() => new Set(loadOpen(who) || []))
  const [phoneG, setPhoneG] = useState(() => new Set(activeGroup ? [activeGroup] : []))
  const [isPhone, setIsPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 1023px)').matches : false)
  const [flyG, setFlyG] = useState(null)   // collapsed rail: { id, top } of the group whose flyout is open
  React.useEffect(() => {
    if (!window.matchMedia) return
    const mq = window.matchMedia('(max-width: 1023px)')
    const on = () => setIsPhone(mq.matches)
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on)
    return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on) }
  }, [])
  React.useEffect(() => { if (isPhone) setCollapsed(false) }, [isPhone])   // turned to portrait: the drawer is the full menu
  React.useEffect(() => { setOpenG(new Set(loadOpen(who) || [])) }, [who])
  React.useEffect(() => {
    if (!activeGroup) return
    setOpenG(prev => { if (prev.has(activeGroup)) return prev; const n = new Set(prev); n.add(activeGroup); saveOpen(who, n); return n })
    setPhoneG(prev => prev.has(activeGroup) ? prev : new Set([...prev, activeGroup]))
  }, [activeGroup, who])
  React.useEffect(() => { if (mobileOpen && !window.__hcTourOn) setPhoneG(new Set(activeGroup ? [activeGroup] : [])) }, [mobileOpen]) // eslint-disable-line
  React.useEffect(() => { setFlyG(null) }, [page, collapsed])
  // the tutorial shows real menu entries (TutorialTour.jsx): opens the drawer on the phone, the rail on iPad, and the group of the entry.
  // Nothing here is remembered: the login's own open groups (saveOpen) stay as they were.
  const tourShell = React.useRef({ isPhone, activeGroup })
  tourShell.current = { isPhone, activeGroup }
  const beforeTour = React.useRef(null)
  React.useEffect(() => {
    const h = ev => {
      const { open, page: p } = (ev && ev.detail) || {}
      const { isPhone: ph } = tourShell.current
      if (open) {
        if (ph) setMobileOpen(true)
        else setCollapsed(c => { if (beforeTour.current === null) beforeTour.current = c; return false })
        const g = p ? groupOf(p) : null
        if (g) { if (ph) setPhoneG(new Set([g])); else setOpenG(prev => (prev.has(g) ? prev : new Set(prev).add(g))) }
      } else {
        setMobileOpen(false)
        if (beforeTour.current !== null) { setCollapsed(beforeTour.current); beforeTour.current = null }
      }
    }
    window.addEventListener('hc-tour-menu', h)
    return () => window.removeEventListener('hc-tour-menu', h)
  }, [])
  const groupIsOpen = id => (isPhone ? phoneG : openG).has(id)
  const toggleGroup = id => {
    if (isPhone) { setPhoneG(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n }); return }
    setOpenG(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); saveOpen(who, n); return n })
  }
  // pages: only the ones in this login's menu - by name, by what they hold, and the pages of the records found (pageSearch.js)
  const searchHits = pageHits(visibleNav, q, recHits)
  const closeSearch = () => { setQ(''); setSearchOpen(false); setRecHits([]); searchRef.current && searchRef.current.blur() }
  const goHit = (id) => { onNavigate(id); closeSearch() }
  const goRec = (hit) => { const pg = openHit(hit); closeSearch(); onNavigate(pg) }
  // phone tab bar: same permission rules as the sidebar
  const tabs = PHONE_TABS.filter(t => visibleNav.some(n => n.id === t.id && !n.soon))
  // hide the tab bar while the phone keyboard is up
  React.useEffect(() => {
    const isField = el => el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)
    const on = e => { if (isField(e.target)) document.body.classList.add('hc-kb') }
    const off = () => setTimeout(() => { if (!isField(document.activeElement)) document.body.classList.remove('hc-kb') }, 50)
    document.addEventListener('focusin', on); document.addEventListener('focusout', off)
    return () => { document.removeEventListener('focusin', on); document.removeEventListener('focusout', off); document.body.classList.remove('hc-kb') }
  }, [])
  const [vpf, setVpf] = React.useState(null)
  const [tzModal, setTzModal] = React.useState(false)
  const [vpfOpen, setVpfOpen] = React.useState(false)
  const [vpfEdit, setVpfEdit] = React.useState(null)
  const [vpfSaving, setVpfSaving] = React.useState(false)
  const VPF_FIELDS = ['IMO','MMSI','Call Sign','Flag','Registration','Home Port','LOA','Beam','Draft','Gross Tonnage','Net Tonnage','Propulsion','Owner','Email','Year Built','Builder','Max Speed','Cruise Speed','Max Draft','Max Air Draft','Class','Hull','Displacement','Engines','Total KW','Billing Name','Billing Address','VAT Number']
  async function saveVpf() {
    if (!vpf || !vpf.id || !vpfEdit) return
    setVpfSaving(true)
    try {
      const patch = {}
      for (const f of VPF_FIELDS) if ((vpfEdit[f] ?? '') !== (vpf[f] ?? '')) patch[f] = vpfEdit[f]
      if (Object.keys(patch).length) {
        // particulars live in Supabase: updateRow maps the labels to columns
        await updateRow('vessel_particulars', vpf.id, patch)
        setVpf({ ...vpf, ...patch })
      }
      setVpfOpen(false)
    } catch (e) { alert('Particulars not saved: ' + (e.message || e)) } finally { setVpfSaving(false) }
  }
  React.useEffect(() => {
    let live = true
    if (atFleet) { setVpf(null); return () => { live = false } }
    fetchRows('vessel_particulars').then(d => { if (live && d && d[0]) setVpf(d[0]) }).catch(() => {})
    return () => { live = false }
  }, [config?.id, atFleet])
  const [tbwx, setTbwx] = React.useState(null)
  React.useEffect(() => {
    let live = true
    const gv = v => (v && typeof v === 'object' && 'value' in v) ? (v.value ?? '') : (v ?? '')
    if (atFleet) { setTbwx(null); return () => { live = false } }
    fetchRows('port_calls').then(rows => {
      if (!live) return
      const today = new Date().toISOString().split('T')[0]
      const wp = (rows || []).map(r => ({ port: gv(r['Port']), country: gv(r['Country']), eta: gv(r['ETA Date']) })).filter(p => p.port)
      const up = wp.filter(p => p.eta && p.eta >= today).sort((a,b) => a.eta.localeCompare(b.eta))
      const chosen = up[0] || wp[wp.length - 1]
      if (!chosen) return
      const params = new URLSearchParams({ port: chosen.port })
      if (chosen.country) params.set('country', chosen.country)
      fetch('/api/weather?' + params.toString()).then(r => r.json()).then(d => { if (live && !d.error) setTbwx({ ...(d.current || d), port: chosen.port }) }).catch(() => {})
    }).catch(() => {})
    return () => { live = false }
  }, [config?.id, atFleet])

  const flyTimer = React.useRef(null)
  const keepFly = () => { clearTimeout(flyTimer.current) }
  const leaveFly = () => { clearTimeout(flyTimer.current); flyTimer.current = setTimeout(() => setFlyG(null), 220) }
  function openFly(id, el) { keepFly(); const r = el.getBoundingClientRect(); setFlyG({ id, top: Math.max(8, Math.min(r.top, window.innerHeight - 40)) }) }
  // one menu entry (sidebar, or the flyout of the collapsed rail)
  function renderItem(item, inFly) {
    const isActive = page === item.id
    const badgeCount = item.badge ? (badges?.[item.badge] || 0) : 0
    const slim = collapsed && !inFly
    const go = () => { item.href ? (window.location.href = item.href) : onNavigate(item.id); if (inFly) setFlyG(null) }
    return (
      <div
        key={item.id}
        data-nav={item.id}
        className="hc-nav"
        role="button"
        tabIndex={0}
        aria-current={isActive ? 'page' : undefined}
        onClick={go}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go() } }}
        style={{
          ...styles.navItem,
          ...(isActive ? styles.navActive : {}),
          justifyContent: slim ? 'center' : 'flex-start',
          padding: slim ? '8px' : '7px 10px',
          outline: 'none',
          border: 'none',
        }}
        title={slim ? item.label : ''}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0, color: isActive ? '#4d84ff' : '#cdd8e8', transition:'color .14s'}}>
          {item.icon ? <use href={'#' + item.icon} /> : null}
        </svg>
        {!slim && <span style={styles.navLabel}>{(item.id === 'dashboard' && accountType === 'company' && !selectedVessel) ? 'Fleet' : item.label}</span>}
        {!slim && badgeCount > 0 && (
          <span style={{
            ...styles.badge,
            background: item.id === 'inventory' ? 'rgba(217,72,15,.22)' : 'rgba(192,67,74,.2)',
            color: item.id === 'inventory' ? '#F2895C' : '#e07070',
          }}>{badgeCount}</span>
        )}
      </div>
    )
  }

  return (
    <div className="hc-shell" data-page={page} style={styles.shell}>
      <style>{`
        @media (max-width: 1023px) {
          .hc-burger { display: block !important; }
          .hc-sidebar {
            position: fixed; top: 0; left: 0; bottom: 0; z-index: 200;
            width: 268px !important;
            transform: translateX(-100%);
            transition: transform .22s ease;
            box-shadow: 0 0 40px rgba(0,0,0,.5);
          }
          .hc-sidebar.hc-open { transform: translateX(0); }
          .hc-scrim { position: fixed; inset: 0; background: rgba(0,0,0,.5); z-index: 150; }
          .hc-header { padding: 0 10px !important; gap: 8px !important; height: 52px !important; position: relative; }
          .hc-header > *:nth-child(n+3) { display: none !important; }
          .hc-header > .hc-em-header { display: inline-flex !important; flex-shrink: 0; }
          .hc-header > .hc-guide-btn { display: inline-flex !important; align-items: center; justify-content: center; margin-left: 0 !important; }
          .hc-header > *:nth-child(2) { flex: 1; min-width: 0; overflow: hidden; }
          .hc-content { padding: 12px !important; }
        }
        @media (max-width: 620px) {
          .hc-hide-sm { display: none !important; }
        }
        @media (max-width: 1023px) {
          .hc-content, .hc-content * { max-width: 100% !important; }
          .hc-content { overflow-x: hidden !important; }
          /* every flex row inside a page wraps */
          .hc-content div[style*="flex"] { flex-wrap: wrap !important; }
          /* ...except a scrolling dialog COLUMN: wrapped, its lower fields became a second column off screen (Documents dialog) */
          .hc-content div.hc-dlg-col { flex-wrap: nowrap !important; }
          /* kill fixed pixel widths on panels */
          .hc-content div[style*="width:3"],
          .hc-content div[style*="width: 3"],
          .hc-content div[style*="width:2"],
          .hc-content div[style*="width: 2"],
          .hc-content aside { width: 100% !important; flex: 1 1 100% !important; min-width: 0 !important; }
          /* viewport-height locks off */
          .hc-content div[style*="100vh"]:not(.fc):not(.fc *) { height: auto !important; max-height: none !important; }
          .hc-content .fc, .hc-content .fc * { max-width: none !important; }
          /* inputs and controls full width, stop overflow */
          .hc-content input[type="text"], .hc-content input:not([type]), .hc-content textarea, .hc-content select { max-width: 100% !important; box-sizing: border-box !important; }
          .hc-content table:not(.fc table) { display: block !important; overflow-x: auto !important; }
          .hc-content .fc table { display: table !important; overflow: visible !important; }
          .hc-content .fc div { flex-wrap: nowrap !important; }
          .hc-content .fc, .hc-content .fc div, .hc-content .fc table, .hc-content .fc td, .hc-content .fc th { max-width: none !important; width: auto; }
          .hc-content .fc .fc-daygrid-body, .hc-content .fc .fc-scrollgrid { width: 100% !important; }
        }
        /* iPhone notch / home bar: installed app (any width) and phones */
        @media (display-mode: standalone) {
          .hc-header { height: calc(66px + env(safe-area-inset-top)) !important; padding-top: env(safe-area-inset-top) !important; }
          .hc-content { padding-bottom: calc(20px + env(safe-area-inset-bottom)) !important; }
        }
        @media (max-width: 1023px), (display-mode: standalone) {
          .hc-shell { height: 100dvh !important; padding-left: env(safe-area-inset-left); padding-right: env(safe-area-inset-right); }
          .hc-sidebar { padding-top: env(safe-area-inset-top); padding-bottom: env(safe-area-inset-bottom); }
        }
        @media (max-width: 1023px) {
          .hc-header { height: calc(52px + env(safe-area-inset-top)) !important; padding-top: env(safe-area-inset-top) !important; }
          .hc-sidebar { padding-left: env(safe-area-inset-left); }
          .hc-content { padding-bottom: calc(12px + env(safe-area-inset-bottom)) !important; }
        }
        /* phone: bottom tab bar replaces the sidebar (More opens the full menu) */
        .hc-tabbar { display: none; }
        @media (max-width: 1023px) {
          .hc-burger { display: none !important; }
          .hc-tabbar {
            display: flex; position: fixed; left: 0; right: 0; bottom: 0; z-index: 160;
            height: calc(58px + env(safe-area-inset-bottom)); padding: 0 env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);
            background: var(--card); border-top: 1px solid var(--line); box-shadow: 0 -4px 18px rgba(0,0,0,.12);
          }
          .hc-tab {
            flex: 1 1 0; min-width: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3px;
            background: none; border: none; padding: 6px 0 4px; font: inherit; font-size: 10.5px; font-weight: 600;
            color: var(--muted); cursor: pointer; -webkit-tap-highlight-color: transparent;
          }
          .hc-tab span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
          .hc-tab-on { color: #2457d6; }
          .hc-content { padding-bottom: calc(70px + env(safe-area-inset-bottom)) !important; }
          body.hc-kb .hc-tabbar { display: none; }
          body.hc-kb .hc-content { padding-bottom: 12px !important; }
        }
      `}</style>
      <svg style={{display:'none'}} xmlns="http://www.w3.org/2000/svg">
        <symbol id="i-grid" viewBox="0 0 24 24"><rect x="3.5" y="3.5" width="7" height="7" rx="1.8"/><rect x="13.5" y="3.5" width="7" height="7" rx="1.8"/><rect x="3.5" y="13.5" width="7" height="7" rx="1.8"/><rect x="13.5" y="13.5" width="7" height="7" rx="1.8"/></symbol>
        <symbol id="i-spark" viewBox="0 0 24 24"><path d="M12 3l1.9 5.6L19.5 10.5l-5.6 1.9L12 18l-1.9-5.6L4.5 10.5l5.6-1.9z"/></symbol>
        <symbol id="i-mega" viewBox="0 0 24 24"><path d="M3.5 10.5v3a1 1 0 001 1h2.2l6.8 4V5.5l-6.8 4H4.5a1 1 0 00-1 1z"/><path d="M17 9.5a4 4 0 010 5"/></symbol>
        <symbol id="i-gear" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3.2"/><path d="M12 3.5v2.6M12 17.9v2.6M3.5 12h2.6M17.9 12h2.6M6 6l1.9 1.9M16.1 16.1L18 18M18 6l-1.9 1.9M7.9 16.1L6 18"/></symbol>
        <symbol id="i-doc-lines" viewBox="0 0 24 24"><rect x="5" y="3.5" width="14" height="17" rx="2"/><path d="M9 8.5h6M9 12h6M9 15.5h3.5"/></symbol>
        <symbol id="i-check-circle" viewBox="0 0 24 24"><circle cx="12" cy="12" r="8.5"/><path d="M8.6 12.2l2.3 2.3 4.5-5"/></symbol>
        <symbol id="i-box" viewBox="0 0 24 24"><path d="M21 8l-9-4.5L3 8v8l9 4.5L21 16z"/><path d="M3 8l9 4.5L21 8M12 12.5V20.5"/></symbol>
        <symbol id="i-file" viewBox="0 0 24 24"><path d="M13.5 3.5H7a2 2 0 00-2 2v13a2 2 0 002 2h10a2 2 0 002-2V9z"/><path d="M13.5 3.5V9H19"/></symbol>
        <symbol id="i-clipboard" viewBox="0 0 24 24"><rect x="5.5" y="4.5" width="13" height="16" rx="2"/><rect x="9" y="2.5" width="6" height="3.6" rx="1.2"/><path d="M9 13l2.2 2.2 4-4.6"/></symbol>
        <symbol id="i-list" viewBox="0 0 24 24"><path d="M8.5 6.5H20M8.5 12H20M8.5 17.5H20"/><circle cx="4.5" cy="6.5" r="1"/><circle cx="4.5" cy="12" r="1"/><circle cx="4.5" cy="17.5" r="1"/></symbol>
        <symbol id="i-ring" viewBox="0 0 24 24"><circle cx="12" cy="12" r="8.5"/><circle cx="12" cy="12" r="3.8"/><path d="M6 6l3.2 3.2M14.8 14.8L18 18M18 6l-3.2 3.2M9.2 14.8L6 18"/></symbol>
        <symbol id="i-alert" viewBox="0 0 24 24"><path d="M12 4L2.8 19.5h18.4z"/><path d="M12 10.2v4"/><circle cx="12" cy="16.8" r=".4"/></symbol>
        <symbol id="i-users" viewBox="0 0 24 24"><circle cx="9.5" cy="8.5" r="3.3"/><path d="M3.5 19.5c0-3.2 2.6-5.3 6-5.3s6 2.1 6 5.3"/><path d="M16 5.7a3 3 0 010 5.6M17.7 14.5c1.9.7 3 2.3 3 4.5"/></symbol>
        <symbol id="i-cal" viewBox="0 0 24 24"><rect x="4" y="5.5" width="16" height="15" rx="2"/><path d="M4 10.5h16M8.5 3.5v3.6M15.5 3.5v3.6"/></symbol>
        <symbol id="i-anchor" viewBox="0 0 24 24"><circle cx="12" cy="5.5" r="2.4"/><path d="M12 7.9V21"/><path d="M4.5 13.5a7.5 7.5 0 0015 0"/><path d="M9 10.5h6"/></symbol>
        <symbol id="i-pin" viewBox="0 0 24 24"><path d="M12 21s-6.8-5.8-6.8-10.7a6.8 6.8 0 0113.6 0C18.8 15.2 12 21 12 21z"/><circle cx="12" cy="10" r="2.4"/></symbol>
        <symbol id="i-bookmark" viewBox="0 0 24 24"><path d="M7 3.5h10a1 1 0 011 1V20.5l-6-4-6 4V4.5a1 1 0 011-1z"/></symbol>
        <symbol id="i-clock" viewBox="0 0 24 24"><circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3.2 1.9"/></symbol>
        <symbol id="i-book" viewBox="0 0 24 24"><path d="M12 6.5c-1.2-1.6-3.2-2.5-5.5-2.5H4v14h3c2 0 4 .8 5 2 1-1.2 3-2 5-2h3V4h-2.5c-2.3 0-4.3.9-5.5 2.5z"/><path d="M12 6.5V20"/></symbol>
        <symbol id="i-cloud" viewBox="0 0 24 24"><path d="M7 18.5a4.3 4.3 0 01-.4-8.6 6 6 0 0111.6 1.6 3.7 3.7 0 01-.7 7z"/></symbol>
        <symbol id="i-shield" viewBox="0 0 24 24"><path d="M12 3.5l7.5 2.8v5.7c0 4.3-3 7.4-7.5 8.5-4.5-1.1-7.5-4.2-7.5-8.5V6.3z"/><path d="M9 11.8l2.1 2.1 3.9-4.3"/></symbol>
        <symbol id="i-compass" viewBox="0 0 24 24"><circle cx="12" cy="12" r="8.5"/><path d="M15.4 8.6l-2.2 4.6-4.6 2.2 2.2-4.6z"/></symbol>
        <symbol id="i-wheel" viewBox="0 0 24 24"><circle cx="12" cy="12" r="2.6"/><circle cx="12" cy="12" r="7.4"/><path d="M12 2v3.6M12 18.4V22M2 12h3.6M18.4 12H22M4.9 4.9l2.5 2.5M16.6 16.6l2.5 2.5M19.1 4.9l-2.5 2.5M7.4 16.6l-2.5 2.5"/></symbol>
        <symbol id="i-wrench" viewBox="0 0 24 24"><path d="M14.6 6.3a3.7 3.7 0 00-4.9 4.4l-5.2 5.2a1.8 1.8 0 002.6 2.6l5.2-5.2a3.7 3.7 0 004.4-4.9l-2.5 2.5-2-.1-.1-2z"/></symbol>
        <symbol id="i-crane" viewBox="0 0 24 24"><path d="M6 21V3.5M4 21h5M6 3.5l13 3.4M6 8l8 2.1"/><path d="M14 10.1v2.4M12.6 12.5h2.8l-1.4 2.1z"/></symbol>
        <symbol id="i-hardhat" viewBox="0 0 24 24"><path d="M4.5 15.5a7.5 7.5 0 0115 0"/><path d="M2.8 15.5h18.4"/><path d="M9.6 8.3V15M14.4 8.3V15"/><path d="M9.6 8.6a5 5 0 014.8 0"/></symbol>
        <symbol id="i-coins" viewBox="0 0 24 24"><ellipse cx="12" cy="6.4" rx="6" ry="2.5"/><path d="M6 6.4v4.1c0 1.4 2.7 2.5 6 2.5s6-1.1 6-2.5V6.4"/><path d="M6 10.5v4.1c0 1.4 2.7 2.5 6 2.5s6-1.1 6-2.5v-4.1"/></symbol>
        <symbol id="i-boat" viewBox="0 0 24 24"><path d="M12 2.5v11"/><path d="M12.8 4.5l4.4 7.5h-4.4z"/><path d="M11.2 6L7 12h4.2"/><path d="M3.5 15h17l-2.4 4.5H5.9z"/></symbol>
      </svg>
      {/* Sidebar */}
      {mobileOpen && <div className="hc-scrim" onClick={() => setMobileOpen(false)} />}
      <aside className={'tsb hc-sidebar' + (mobileOpen ? ' hc-open' : '')} style={{...styles.sidebar, width: collapsed ? 56 : 268}}>
        {/* Brand */}
        {!collapsed && (
          <div style={styles.brand}>
            <div style={styles.brandMark}>
              <div className="hc-logo-waves" aria-hidden="true"><span/><span/><span/></div>
              <svg width="26" height="22" viewBox="0 0 32 28" fill="none" style={{flexShrink:0, position:'relative', zIndex:1}}>
                <path d="M3 10 Q7.5 5 12 10 T21 10 T30 10" stroke="#4d84ff" strokeWidth="2.8" strokeLinecap="round"/>
                <path d="M3 18 Q7.5 13 12 18 T21 18 T30 18" stroke="#4d84ff" strokeWidth="2.8" strokeLinecap="round"/>
              </svg>
            </div>
            <div>
              <div style={styles.brandName}>HEYCAPTAIN</div>
              <div style={styles.vesselName}>{config?.vessel_name || 'My Vessel'}</div>
            </div>
            <button style={{...styles.collapseBtn, marginLeft: 'auto'}} onClick={() => setCollapsed(!collapsed)} title="Toggle sidebar">
              ‹
            </button>
          </div>
        )}
        {!collapsed && <div style={{height:1, margin:'0 18px', background:'linear-gradient(90deg, rgba(46,91,255,.35), rgba(46,91,255,.04))'}} />}
        {collapsed && (
          <div style={{display:'flex', justifyContent:'center', padding:'12px 0', borderBottom:'0.5px solid var(--border)'}}>
            <button style={styles.collapseBtn} onClick={() => setCollapsed(!collapsed)} title="Toggle sidebar">
              ›
            </button>
          </div>
        )}

        {/* Emergency: a Contingency Plan in 2 taps from anywhere (src/components/EmergencyLauncher.jsx) */}
        <div className="hc-em-slot" style={{ padding: collapsed ? '10px 0' : '10px 14px 2px', display: 'flex', justifyContent: 'center' }}>
          <EmergencyButton place="sidebar" compact={collapsed} style={collapsed ? {} : { width: '100%', minHeight: 40 }} />
        </div>

        {/* Nav: pinned entries, then collapsible groups (src/lib/navGroups.js). Collapsed rail: one icon per group with a flyout. */}
        <nav style={styles.nav} aria-label="Sidebar" onMouseLeave={() => collapsed && leaveFly()}>
          {menu.pinned.map(item => renderItem(item, false))}
          {menu.groups.length > 0 && menu.pinned.length > 0 && <div style={{ height: collapsed ? 10 : 6 }} />}
          {menu.groups.map(g => {
            const open = groupIsOpen(g.id)
            const hasActive = g.items.some(it => it.id === page)
            const gb = groupBadge(g.items, badges)
            const pill = gb.count > 0 && (collapsed || !open)
            const pillStyle = { ...styles.badge, background: gb.tone === 'red' ? 'rgba(192,67,74,.2)' : 'rgba(217,72,15,.22)', color: gb.tone === 'red' ? '#e07070' : '#F2895C' }
            if (collapsed) {
              return (
                <div key={g.id} style={{ position: 'relative' }}
                  onMouseEnter={e => openFly(g.id, e.currentTarget)}>
                  <button type="button" className="hc-nav hc-grp-rail" aria-haspopup="true" aria-expanded={flyG?.id === g.id}
                    aria-label={g.label + (gb.count > 0 ? ', ' + gb.count + (gb.tone === 'red' ? ' need attention' : '') : '')}
                    title={g.label}
                    onClick={e => (flyG?.id === g.id ? setFlyG(null) : openFly(g.id, e.currentTarget.parentNode))}
                    onKeyDown={e => { if (e.key === 'Escape') setFlyG(null) }}
                    style={{ ...styles.navItem, ...(hasActive ? styles.navActive : {}), justifyContent: 'center', padding: '8px', font: 'inherit', fontSize: 13, outline: 'none', border: 'none' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: hasActive ? '#4d84ff' : '#cdd8e8' }}><use href={'#' + g.icon} /></svg>
                    {pill && <span style={{ ...pillStyle, position: 'absolute', top: 1, right: 3, fontSize: 8, padding: '0 4px', lineHeight: '13px' }}>{gb.count}</span>}
                  </button>
                </div>
              )
            }
            return (
              <div key={g.id} className="hc-grp">
                <button type="button" className="hc-grp-head" aria-expanded={open} aria-controls={'hc-grp-' + g.id} onClick={() => toggleGroup(g.id)}
                  style={{ ...styles.secLabel, ...styles.grpHead, ...(hasActive && !open ? { color: '#9bbcff' } : {}) }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><use href={'#' + g.icon} /></svg>
                  <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.label}</span>
                  {pill && <span style={{ ...pillStyle, letterSpacing: 0 }} aria-label={gb.count + (gb.tone === 'red' ? ' need attention' : '')}>{gb.count}</span>}
                  <span aria-hidden="true" style={{ fontSize: 11, width: 12, textAlign: 'center', letterSpacing: 0 }}>{open ? '▾' : '▸'}</span>
                </button>
                <div id={'hc-grp-' + g.id} className={'hc-grp-body' + (open ? ' hc-grp-open' : '')}>
                  <div className="hc-grp-inner">{g.items.map(item => renderItem(item, false))}</div>
                </div>
              </div>
            )
          })}
        </nav>
        {collapsed && flyG && (() => {
          const g = menu.groups.find(x => x.id === flyG.id)
          if (!g) return null
          return (
            <div className="hc-fly" role="menu" aria-label={g.label} style={{ top: flyG.top }}
              onMouseEnter={keepFly} onMouseLeave={leaveFly} onKeyDown={e => { if (e.key === 'Escape') setFlyG(null) }}>
              <div style={{ ...styles.secLabel, padding: '4px 10px 6px' }}>{g.label}</div>
              {g.items.map(item => renderItem(item, true))}
            </div>
          )
        })()}

        {!collapsed && (
          <div style={{padding:'6px 12px 0', fontSize:10.5, color:'#7E90AB', display:'flex', gap:10}}>
            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" style={{color:'#7E90AB', textDecoration:'none'}}>Privacy</a>
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" style={{color:'#7E90AB', textDecoration:'none'}}>Terms</a>
            <a href={LICENCES_PATH} target="_blank" rel="noopener noreferrer" style={{color:'#7E90AB', textDecoration:'none'}}>Open-source licences</a>
          </div>
        )}
        {/* Footer */}
        <div style={styles.sbFooter}>
          {!collapsed ? (
            <div style={{display:'flex', alignItems:'center', gap:10, width:'100%'}}>
              <UserMenu style={{display:'flex', alignItems:'center', gap:10, minWidth:0, flex:'0 1 auto'}}>
              <div style={{width:32, height:32, borderRadius:'50%', flexShrink:0, background:'linear-gradient(135deg, #4d84ff, #1B4FD8)', color:'#ffffff', fontSize:11, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center'}}>
                {((accountType === 'crew' && config?.crewName) || config?.vessel_name || 'HC').replace(/[^A-Za-z ]/g,'').split(' ').filter(Boolean).slice(0,2).map(w => w[0]).join('').toUpperCase() || 'HC'}
              </div>
              <div style={{minWidth:0}}>
                <div style={{fontSize:12, fontWeight:600, lineHeight:1.25, color:'#E7EDF6', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{accountType === 'crew' && config?.crewName ? config.crewName + ' \u00b7 ' + (config?.vessel_name || '') : (config?.vessel_name || 'My Vessel')}</div>
                <div style={{fontSize:10.5, color:'#7E90AB', display:'flex', alignItems:'center', gap:5}}>
                  <span style={{width:6, height:6, borderRadius:'50%', background:'#35C98B', boxShadow:'0 0 6px rgba(53,201,139,.7)', animation:'pulse 2.4s infinite'}} />{accountType === 'company' ? 'Fleet Manager' : accountType === 'crew' ? roleLabel(config?.role) : 'Captain'} · Online
                </div>
              </div>
              </UserMenu>
              <FeedbackWidget page={page} style={{marginLeft:'auto'}} />
              <button title="Sign out" onClick={signOut} style={{ background:'transparent', border:'1px solid rgba(255,255,255,.12)', color:'#7E90AB', fontSize:13, cursor:'pointer', width:28, height:28, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}>⏻</button>
            </div>
          ) : (
            <div style={{display:'flex', flexDirection:'column', alignItems:'center', gap:6, margin:'0 auto'}}>
            <UserMenu compact style={{ background:'transparent', border:'1px solid rgba(255,255,255,.12)', color:'#7E90AB', fontSize:13, cursor:'pointer', width:28, height:28, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center' }}>{'\u2699'}</UserMenu>
            <FeedbackWidget page={page} />
            <button title="Sign out" onClick={signOut} style={{ background:'transparent', border:'1px solid rgba(255,255,255,.12)', color:'#7E90AB', fontSize:13, cursor:'pointer', width:28, height:28, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center'}}>⏻</button>
            </div>
          )}
        </div>
      </aside>

      {/* Main */}
      <main style={{...styles.main, ...(page === 'calendar' ? {overflow: 'visible'} : {}), ...(atFleet && page !== 'chat' ? FLEET_DARK : {}), ...(accountType === 'company' ? { transition: 'background-color .35s ease' } : {})}}>
        {/* Page header */}
        <header className="hc-header" style={styles.header}>
          <button className="hc-burger" onClick={() => setMobileOpen(o => !o)} aria-label="Menu"
            style={{ display: 'none', background: 'none', border: 'none', color: 'var(--text)', fontSize: 20, cursor: 'pointer', padding: '4px 6px', lineHeight: 1 }}>
            &#9776;
          </button>
          <div style={{flex:1}}>
            <div style={styles.pageTitle}>{atFleet ? 'Fleet' : title}</div>
            {(atFleet || subtitle) && <div style={styles.pageSub}>{atFleet ? 'Fleet overview' : subtitle}</div>}
          </div>
          <PageGuideButton page={atFleet ? 'fleet' : page} />
          {!atFleet && <EmergencyButton place="header" />}
          {tbwx && !atFleet && (
            <div onClick={() => onNavigate('weather')} style={{display:'flex', alignItems:'center', gap:14, marginLeft:8, cursor:'pointer', color:'var(--muted)', fontSize:12.5}}>
              <span style={{display:'flex', alignItems:'center', gap:6, fontWeight:600, color:'var(--text)'}}>☀ {tbwx.tempC != null ? Math.round(tbwx.tempC*10)/10 + '°' : '–'}</span>
              <span aria-hidden="true" style={{display:'inline-block', width:1, height:12, background:'var(--line-2)', verticalAlign:'middle'}} />
              <span>Wind <b style={{color:'var(--text)', fontWeight:600}}>{tbwx.windKn != null ? Math.round(tbwx.windKn) + ' kn' : '–'}</b>{tbwx.windCompass ? ' ' + tbwx.windCompass : ''}</span>
              <span aria-hidden="true" style={{display:'inline-block', width:1, height:12, background:'var(--line-2)', verticalAlign:'middle'}} />
              <span>Waves <b style={{color:'var(--text)', fontWeight:600}}>{tbwx.waveM != null ? Math.round(tbwx.waveM*10)/10 + ' m' : '–'}</b></span>
              <span aria-hidden="true" style={{display:'inline-block', width:1, height:12, background:'var(--line-2)', verticalAlign:'middle'}} />
              <span style={{fontWeight:600, color:'var(--muted)'}}>{tbwx.port} ›</span>
            </div>
          )}
          <div style={{position:'relative', marginRight:14}}>
            <div className="tb-search" style={{display:'flex', alignItems:'center', gap:9, width:260, padding:'8px 13px', background:'var(--bg)', border:'1px solid var(--line)', borderRadius:10, fontSize:12.5}}>
              <span style={{fontSize:13, color:'var(--faint)'}}>⌕</span>
              <input
                ref={searchRef}
                value={q}
                onChange={e => { setQ(e.target.value); setSearchOpen(true) }}
                onFocus={() => setSearchOpen(true)}
                onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
                onKeyDown={e => { if (e.key === 'Enter') { const to = enterTarget(searchHits, recHits); if (to && to.page) goHit(to.page); else if (to && to.record) goRec(to.record) } }}
                placeholder="Search pages and records…"
                style={{flex:1, minWidth:0, border:'none', outline:'none', background:'transparent', color:'var(--text)', fontSize:12.5, fontFamily:'inherit'}}
              />
              <kbd style={{fontFamily:'inherit', fontSize:10, color:'var(--faint)', border:'1px solid var(--line-2)', borderRadius:5, padding:'1px 5px', background:'transparent'}} title="Press / to search">/</kbd>
            </div>
            {searchOpen && (searchHits.length > 0 || recHits.length > 0) && (
              <div style={{position:'absolute', top:'calc(100% + 4px)', left:0, width:320, maxHeight:'70vh', overflowY:'auto', background:'var(--card)', border:'1px solid var(--line)', borderRadius:10, boxShadow:'0 8px 24px -8px rgba(0,0,0,.4)', overflow:'hidden', zIndex:2500}}>
                {searchHits.map(hit => (
                  <div
                    key={hit.id}
                    onMouseDown={() => goHit(hit.id)}
                    style={{display:'flex', alignItems:'center', gap:10, padding:'9px 13px', cursor:'pointer', fontSize:13, color:'var(--text)'}}
                    onMouseEnter={e => e.currentTarget.style.background='var(--bg)'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}
                  >
                    <span style={{width:22, height:22, borderRadius:6, background:hit.color, color:hit.tc, fontSize:9, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}>{hit.ic}</span>
                    <span>{hit.label}</span>
                  </div>
                ))}
                {recHits.length > 0 && <div style={{padding:'7px 13px 3px', fontSize:10, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--faint)', borderTop: searchHits.length ? '1px solid var(--line)' : 'none'}}>Records</div>}
                {recHits.map(hit => (
                  <div
                    key={hit.kind + hit.id}
                    onMouseDown={() => goRec(hit)}
                    style={{display:'flex', alignItems:'center', gap:10, padding:'8px 13px', cursor:'pointer', fontSize:13, color:'var(--text)'}}
                    onMouseEnter={e => e.currentTarget.style.background='var(--bg)'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}
                  >
                    <span style={{fontSize:10, fontWeight:700, color:'var(--muted)', minWidth:62, flexShrink:0}}>{hit.kind}</span>
                    <span style={{minWidth:0, overflow:'hidden'}}>
                      <span style={{display:'block', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{hit.label}</span>
                      {hit.sub && <span style={{display:'block', fontSize:11, color:'var(--faint)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{hit.sub}</span>}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {searchOpen && q.trim() && searchHits.length === 0 && recHits.length === 0 && (
              <div style={{position:'absolute', top:'calc(100% + 4px)', left:0, width:320, background:'var(--card)', border:'1px solid var(--line)', borderRadius:10, padding:'11px 13px', fontSize:12.5, color:'var(--faint)', zIndex:2500}}>{recBusy ? 'Searching\u2026' : 'Nothing matches "' + q.trim() + '"'}</div>
            )}
          </div>
          <div style={{display:'flex',gap:14,alignItems:'center'}}>
            <HeaderClock />
            {false && <ThemeToggle />}
            {onRefresh && (
              <button className="btn" onClick={onRefresh} style={{fontSize:12}}>
                Refresh
              </button>
            )}
            {showNew && (
              <button className="btn btn-primary" onClick={onNew}>
                + New record
              </button>
            )}
          </div>
        </header>

        {vpf && !atFleet && (
          <div onClick={() => { setVpfEdit({ ...vpf }); setVpfOpen(true) }} style={{display:'flex', alignItems:'center', gap:0, flexWrap:'wrap', padding:'9px 28px', background:'var(--card)', borderBottom:'1px solid var(--line)', cursor:'pointer'}}>
            <span style={{fontSize:11.5, fontWeight:700, color:'var(--text)', marginRight:16}}>{config?.vessel_name || 'Vessel'}</span>
            {[['Flag', vpf['Flag']], ['IMO', vpf['IMO']], ['LOA', vpf['LOA']], ['GT', vpf['Gross Tonnage']], ['Class', vpf['Class']], ['Built', vpf['Year Built']]].filter(([,v]) => v).map(([k, v], i) => (
              <span key={k} style={{fontSize:11.5, color:'var(--muted)', marginRight:18, display:'inline-flex', alignItems:'center', gap:5}}>
                <span style={{color:'var(--muted)'}}>{k}</span><span style={{color:'var(--text)', fontWeight:600}}>{v}</span>
              </span>
            ))}
            <span style={{marginLeft:'auto', display:'inline-flex', alignItems:'center', gap:12}}>
              <span style={{fontSize:10.5, color:'var(--muted)'}}>edit particulars {'\u25b8'}</span>
              <button
                title="Ship's time"
                onClick={e => { e.stopPropagation(); setTzModal(true) }}
                style={{ display:'inline-flex', alignItems:'center', gap:5, background:'transparent', border:'1px solid var(--line)', borderRadius:6, padding:'3px 9px', cursor:'pointer', color:'var(--muted)', fontSize:10.5, letterSpacing:'.04em' }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3.2 1.9"/></svg>
                {config?.timezone || "Set ship's time"}
              </button>
              <span style={{color:'var(--faint)', fontSize:14}}>&#8250;</span>
            </span>
          </div>
        )}
        {vpfOpen && vpfEdit && (
          <div onClick={() => setVpfOpen(false)} style={{position:'fixed', inset:0, background:'rgba(10,22,40,.5)', display:'flex', alignItems:'flex-start', justifyContent:'center', zIndex:200, padding:'40px 16px', overflowY:'auto'}}>
            <div onClick={e => e.stopPropagation()} style={{background:'var(--card)', borderRadius:14, padding:22, width:'100%', maxWidth:640, boxShadow:'0 20px 60px rgba(10,22,40,.3)'}}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14}}>
                <span style={{fontSize:16, fontWeight:700, color:'var(--text)'}}>{config?.vessel_name || 'Vessel'} {'\u2014'} Particulars</span>
                <button onClick={() => setVpfOpen(false)} style={{background:'transparent', border:'none', fontSize:18, cursor:'pointer', color:'var(--muted)'}}>{'\u00d7'}</button>
              </div>
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, maxHeight:'60vh', overflowY:'auto', paddingRight:4}}>
                {VPF_FIELDS.map(f => (
                  <label key={f} style={{display:'flex', flexDirection:'column', gap:3, ...(f === 'Billing Address' ? {gridColumn:'1 / -1'} : {})}}>
                    <span style={{fontSize:10, fontWeight:600, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.05em'}}>{f}{f === 'Billing Name' ? ' (printed on purchase orders)' : ''}</span>
                    {f === 'Billing Address'
                      ? <textarea rows={3} value={vpfEdit[f] ?? ''} onChange={e => setVpfEdit(v => ({ ...v, [f]: e.target.value }))}
                          style={{padding:'7px 9px', borderRadius:7, border:'1px solid var(--line)', background:'var(--bg2, var(--card))', color:'var(--text)', fontSize:12.5, resize:'vertical', fontFamily:'inherit'}} />
                      : <input value={vpfEdit[f] ?? ''} onChange={e => setVpfEdit(v => ({ ...v, [f]: e.target.value }))}
                          style={{padding:'7px 9px', borderRadius:7, border:'1px solid var(--line)', background:'var(--bg2, var(--card))', color:'var(--text)', fontSize:12.5}} />}
                  </label>
                ))}
              </div>
              <div style={{display:'flex', gap:8, justifyContent:'flex-end', marginTop:16}}>
                <button className="btn" onClick={() => setVpfOpen(false)}>Cancel</button>
                <button className="btn btn-primary" disabled={vpfSaving} onClick={saveVpf}>{vpfSaving ? 'Saving...' : 'Save particulars'}</button>
              </div>
            </div>
          </div>
        )}
        {tzModal && <ShipTimeModal config={config} onClose={() => setTzModal(false)} />}
        {/* Content */}
        <div className="hc-content" style={{...styles.content, ...(page === 'calendar' ? {padding: 0, overflow: 'visible'} : {})}}>
          {children}
        </div>
      </main>
      <nav className="hc-tabbar" aria-label="Main">
        {tabs.map(t => (
          <button key={t.id} className={'hc-tab' + (page === t.id && !mobileOpen ? ' hc-tab-on' : '')} onClick={() => onNavigate(t.id)}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><use href={'#' + t.icon} /></svg>
            <span>{t.label}</span>
          </button>
        ))}
        <button className={'hc-tab' + (mobileOpen || !tabs.some(t => t.id === page) ? ' hc-tab-on' : '')} onClick={() => setMobileOpen(o => !o)} aria-label="More">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M4 7h16M4 12h16M4 17h16"/></svg>
          <span>More</span>
        </button>
      </nav>
      <UndoToast />
    </div>
  )
}

const FLEET_DARK = {
  background: '#0a1322',
  '--card': '#101c31', '--text': '#e7edf6', '--muted': '#8fa1bb', '--faint': '#5d6f8a',
  '--line': 'rgba(255,255,255,.08)', '--line-2': 'rgba(255,255,255,.14)', '--border': 'rgba(255,255,255,.08)',
  '--bg': '#0c1627', '--bg2': '#0c1627',
  // the assistant panel opened on the Fleet page (Fleet briefing) sits in here too: its bubbles / inputs use these (fleet-1)
  '--hull': '#101c31', '--ink': '#0c1627', '--ink2': '#101c31', '--sea': '#0c1627', '--horizon': 'rgba(255,255,255,.08)',
  '--border2': 'rgba(255,255,255,.14)', '--chip': 'rgba(59,123,255,.16)',
}

const styles = {
  shell: {
    display: 'flex',
    height: '100vh',
    overflow: 'hidden',
    background: 'var(--ink)',
  },
  sidebar: {
    borderRight: '1px solid rgba(255,255,255,.05)',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    transition: 'width .2s ease',
    overflow: 'hidden',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '16px 12px 14px',
    borderBottom: '1px solid rgba(255,255,255,.07)',
    minHeight: 64,
  },
  brandMark: {
    width: 40, height: 40, position: 'relative',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, borderRadius: 11, overflow: 'hidden',
    background: 'linear-gradient(135deg, rgba(47,107,255,.20), rgba(47,107,255,.04))',
    border: '1px solid rgba(77,132,255,.38)',
    boxShadow: '0 0 14px rgba(47,107,255,.28), inset 0 0 8px rgba(47,107,255,.14)',
  },
  brandName: {
    fontFamily: 'DM Sans, sans-serif',
    fontSize: 16, fontWeight: 700, color: '#ffffff',
    letterSpacing: '.06em', lineHeight: 1.15,
  },
  vesselName: {
    fontSize: 10, fontWeight: 600, color: '#6aaad4',
    letterSpacing: '.14em', textTransform: 'uppercase', marginTop: 2,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160,
  },
  collapseBtn: {
    marginLeft: 'auto', background: 'transparent', border: 'none',
    color: '#7E90AB', cursor: 'pointer', fontSize: 16,
    padding: '2px 4px', borderRadius: 4, flexShrink: 0,
    transition: 'color .13s',
  },
  nav: {
    flex: 1, padding: '8px 6px', display: 'flex', flexDirection: 'column', gap: 1, overflowY: 'auto',
  },
  secLabel: {
    fontSize: 9.5, fontWeight: 700, color: '#7E90AB', letterSpacing: '.18em',
    textTransform: 'uppercase', padding: '16px 10px 6px',
  },
  grpHead: {
    display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '11px 10px 7px', margin: 0,
    background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit', outline: 'none',
  },
  navItem: {
    display: 'flex', alignItems: 'center', gap: 11,
    borderRadius: 9, cursor: 'pointer',
    fontSize: 13, fontWeight: 500, color: 'rgba(231,237,246,.82)',
    transition: 'all .13s', border: 'none',
    background: 'transparent', width: '100%', textAlign: 'left',
    outline: 'none',
  },
  navActive: {
    background: 'linear-gradient(90deg, rgba(47,107,255,.20), rgba(47,107,255,.05))', color: '#9bbcff',
    border: '1px solid transparent', fontWeight: 600,
    boxShadow: 'inset 3px 0 0 0 #2f6bff',
  },
  navIc: {
    width: 24, height: 24, borderRadius: 5,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 8, fontWeight: 700, flexShrink: 0,
  },
  navLabel: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  badge: {
    fontSize: 9, padding: '1px 5px', borderRadius: 8, fontWeight: 500,
  },
  sbFooter: {
    padding: '10px 12px',
    borderTop: '1px solid rgba(255,255,255,.07)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    minHeight: 44,
  },
  signoutBtn: {
    background: 'transparent', border: '1px solid rgba(255,255,255,.12)',
    color: '#7E90AB', fontSize: 10, cursor: 'pointer',
    padding: '3px 8px', borderRadius: 5, fontFamily: 'inherit',
    transition: 'all .13s',
  },
  main: {
    flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0,
  },
  header: {
    height: 66, flexShrink: 0,
    background: 'var(--card)', borderBottom: '1px solid var(--line)',
    display: 'flex', alignItems: 'center', gap: 18,
    padding: '0 28px',
  },
  pageTitle: {
    fontSize: 16.5, fontWeight: 700, letterSpacing: '-.015em', color: 'var(--text)',
  },
  pageSub: {
    fontSize: 11.5, color: 'var(--muted)', marginTop: 1,
  },
  content: {
    flex: 1, overflow: 'auto', padding: '20px',
  },
}
